import React from 'react';
import { isRouteErrorResponse, useRouteError } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react';

// Utils
import { buildRouteErrorCapture } from '../../../utils/routeErrorCapture';

// Assets
import RoisLogo from '../../../assets/logo.svg';

// Components
import { ErrorPanel } from '../../../components/display/ErrorPanel';

// Components Styled
import { Container, Logo } from './style';

/**
 * Pantalla de error SUELTA: el `errorElement` de la raíz del router. Sale en
 * tres situaciones —una URL que no casa con ninguna ruta y NO hay sesión, un
 * fallo en el bloque de login, y cuando revienta el loader o la action de
 * cualquier pantalla—, así que se pinta fuera de `MainLayout` y no puede
 * llevar cabecera: montarla obligaría a ejecutar los loaders del layout, que
 * son justo lo que puede haber fallado.
 *
 * El 404 de alguien que SÍ tiene la sesión abierta no llega hasta aquí: lo
 * recoge `pages/utils/NotFound` dentro de la aplicación, con su cabecera y su
 * menú (ver el `path="*"` del bloque de `MainLayout` en `router/index.tsx`).
 */
export default function ErrorPage(): JSX.Element {
  const routeError = useRouteError();
  const error = routeError as { statusText?: string; message?: string };

  // Una dirección que no existe NO es un fallo de la aplicación, y llamarlo
  // "Error general" manda a buscar una avería que no hay. El motivo crudo
  // tampoco se enseña en ese caso: es "Not Found", inglés de la librería de
  // rutas, que no le dice nada a un operario. En los fallos de verdad sí se
  // conserva — ahí es la única pista que queda para diagnosticar.
  const isRouteResponse = isRouteErrorResponse(routeError);
  const isNotFound = isRouteResponse && routeError.status === 404;

  // 🔴 Sin esto, un fallo de loader NO llega a Sentry como Issue. El data
  // router ATRAPA lo que lanza el loader y pinta esta pantalla; al atraparlo
  // deja de ser excepción no gestionada, así que ni `window.onerror` ni el
  // `Sentry.ErrorBoundary` de `App.tsx` —que solo ve errores de RENDER— se
  // enteran. Son 144 `loader={}` y 45 `action={}`: la vía de datos entera.
  //
  // El `console.error` se conserva: sigue yendo al cubo de Logs por
  // `consoleLoggingIntegration`, y en local es lo que se ve en la consola. Lo
  // que añade la captura es el Issue agrupado, el stack trace resuelto con
  // source maps, las alertas y el seguimiento por release.
  //
  // Va en `useEffect` y no en el cuerpo para no capturar una vez por render.
  React.useEffect(() => {
    console.error(routeError);
    const capture = buildRouteErrorCapture(routeError, isRouteResponse);
    if (!capture) return;
    Sentry.captureException(capture.error, {
      tags: { errorSource: 'routeError', routeErrorKind: capture.kind },
      contexts: {
        routeError: {
          status: capture.status ?? null,
          pathname: window.location.pathname,
        },
      },
    });
  }, [routeError, isRouteResponse]);

  return (
    <ErrorScreen
      isNotFound={isNotFound}
      detail={isNotFound ? undefined : error.statusText || error.message}
    />
  );
}

/** La pantalla en sí, sin saber de dónde sale el error. */
function ErrorScreen({
  isNotFound,
  detail,
}: {
  isNotFound: boolean;
  detail?: string;
}): JSX.Element {
  const { t } = useTranslation(['errors', 'common', 'buttons', 'auth']);
  return (
    <Container>
      <Logo src={RoisLogo} alt={t('auth:login.roisLogo')} />
      <ErrorPanel
        title={isNotFound ? t('errors:pageNotFound') : t('errors:generalError')}
        message={isNotFound ? undefined : t('common:messages.error')}
        detail={detail}
        actionLabel={t('buttons:back')}
        // A la home y con RECARGA DURA, no `navigate()`: aquí algo ya reventó y
        // una navegación de cliente se lleva puesto el estado roto (incluida la
        // posibilidad de volver a entrar en el mismo loader que falló). `/`
        // pasa por `LoaderIndexPage`, que reparte por rol —y manda al login si
        // no hay sesión—. `navigate(-1)` tampoco vale: si el operario llegó por
        // un enlace viejo, "atrás" es la MISMA URL rota.
        onAction={() => {
          window.location.href = '/';
        }}
      />
    </Container>
  );
}

/**
 * Respaldo del límite de errores de la RAÍZ (`App.tsx`), que envuelve también
 * lo que vive fuera del router: los modales, los avisos, la barra de
 * conexión. 🔴 No puede usar `useRouteError` —fuera de un data router
 * revienta— (QA 15-oct, T2.10+vacío: un fallo en un modal acababa en pantalla
 * en blanco porque esta pantalla también caía). El error lo da el propio
 * límite, que ya lo ha mandado a Sentry.
 */
export function AppErrorFallback({ error }: { error: unknown }): JSX.Element {
  const detail =
    error instanceof Error ? error.message : String(error ?? '') || undefined;
  return <ErrorScreen isNotFound={false} detail={detail} />;
}
